import React, { useState, useEffect, useRef } from 'react';
import { Send, Bot, User, Loader2, Paperclip, X, Image as ImageIcon, Phone, CheckCircle2, Plus } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5 MB

const ChatPage = () => {
  const { language, t } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    const saved = localStorage.getItem('pt_chat_session_id');
    if (saved) return saved;
    const fresh = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('pt_chat_session_id', fresh);
    return fresh;
  });
  const [pendingImage, setPendingImage] = useState(null); // { dataUrl, name }
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escName, setEscName] = useState('');
  const [escPhone, setEscPhone] = useState('');
  const [escUrgency, setEscUrgency] = useState('today');
  const [escNotes, setEscNotes] = useState('');
  const [escSubmitting, setEscSubmitting] = useState(false);
  const [escalated, setEscalated] = useState(() => {
    return localStorage.getItem(`pt_chat_escalated_${localStorage.getItem('pt_chat_session_id') || ''}`) === '1';
  });
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        const response = await axios.get(`${API}/chat/history/${sessionId}`);
        setMessages(response.data);
      } catch (error) {
        console.error('Error loading chat history:', error);
      }
    };
    loadHistory();
  }, [sessionId]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t({ el: 'Επιτρέπονται μόνο εικόνες', en: 'Only images are allowed' }));
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(t({ el: 'Η εικόνα πρέπει να είναι έως 5MB', en: 'Image must be up to 5MB' }));
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      setPendingImage({ dataUrl: ev.target.result, name: file.name });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!inputMessage.trim() && !pendingImage) return;

    const userMessage = {
      role: 'user',
      content: inputMessage || (pendingImage ? t({ el: '[Εικόνα]', en: '[Image]' }) : ''),
      timestamp: new Date().toISOString(),
      image: pendingImage?.dataUrl,
    };

    setMessages((prev) => [...prev, userMessage]);
    const sentText = inputMessage;
    const sentImage = pendingImage?.dataUrl || null;
    setInputMessage('');
    setPendingImage(null);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: sentText,
        language,
        image_base64: sentImage,
      });

      setMessages((prev) => [...prev, response.data]);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(
        t({ el: 'Σφάλμα κατά την αποστολή μηνύματος', en: 'Error sending message' })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewConversation = () => {
    if (!window.confirm(t({
      el: 'Σίγουρα θέλετε να ξεκινήσετε νέα συνομιλία; Η τρέχουσα θα κλείσει.',
      en: 'Are you sure you want to start a new conversation? The current one will close.',
    }))) return;
    const oldId = sessionId;
    localStorage.removeItem(`pt_chat_escalated_${oldId}`);
    const fresh = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('pt_chat_session_id', fresh);
    setSessionId(fresh);
    setMessages([]);
    setEscalated(false);
    setPendingImage(null);
    setInputMessage('');
  };

  const handleEscalate = async (e) => {
    e.preventDefault();
    if (!escName.trim() || !escPhone.trim()) return;
    setEscSubmitting(true);
    try {
      await axios.post(`${API}/chat/escalate`, {
        session_id: sessionId,
        contact_name: escName.trim(),
        contact_phone: escPhone.trim(),
        urgency: escUrgency,
        notes: escNotes.trim() || null,
        language,
      });
      toast.success(t({
        el: 'Το αίτημα στάλθηκε στον τεχνικό!',
        en: 'Request sent to the technician!',
      }));
      setEscalated(true);
      localStorage.setItem(`pt_chat_escalated_${sessionId}`, '1');
      setEscalateOpen(false);
      setEscName('');
      setEscPhone('');
      setEscNotes('');
    } catch {
      toast.error(t({ el: 'Σφάλμα αποστολής αιτήματος', en: 'Failed to send request' }));
    } finally {
      setEscSubmitting(false);
    }
  };

  return (
    <div className="pt-20 min-h-screen bg-slate-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-slate-800 border border-slate-700 p-6 mb-6 flex items-center justify-between" data-testid="chat-header">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center">
              <Bot className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">
                {t({ el: 'ΤΕΧΝΙΚΗ ΥΠΟΣΤΗΡΙΞΗ AI', en: 'AI TECHNICAL SUPPORT' })}
              </h1>
              <p className="text-slate-400">
                {t({
                  el: 'Πείτε μας τη βλάβη και το τηλέφωνό σας — θα σας καλέσουμε άμεσα',
                  en: 'Tell us the problem and your phone — we will call you right away',
                })}
              </p>
            </div>
          </div>
          {messages.length > 0 && (
            <button
              type="button"
              onClick={handleNewConversation}
              data-testid="chat-new-conversation-btn"
              title={t({ el: 'Νέα συνομιλία', en: 'New conversation' })}
              className="bg-slate-700 hover:bg-slate-600 text-slate-300 px-3 py-2 text-sm flex items-center space-x-2 flex-shrink-0"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">
                {t({ el: 'Νέα συνομιλία', en: 'New' })}
              </span>
            </button>
          )}
        </div>

        {/* Chat Messages */}
        <div
          className="bg-slate-800 border border-slate-700 p-6 mb-6 h-[600px] overflow-y-auto"
          data-testid="chat-messages-container"
        >
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center" data-testid="chat-welcome">
              <Bot className="w-20 h-20 text-orange-500 mb-6" />
              <h2 className="text-2xl font-bold text-white mb-4">
                {t({ el: 'Καλωσήρθατε!', en: 'Welcome!' })}
              </h2>
              <p className="text-slate-400 max-w-md">
                {t({
                  el: 'Πείτε μου τη βλάβη σε μία πρόταση και το τηλέφωνό σας. Θα σας καλέσουμε άμεσα.',
                  en: 'Tell me the problem in one sentence and your phone number. We will call you right away.',
                })}
              </p>
            </div>
          )}

          <div className="space-y-6">
            {messages.map((message, index) => (
              <div
                key={index}
                data-testid={`chat-message-${index}`}
                className={`flex items-start space-x-3 ${
                  message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.role === 'user' ? 'bg-slate-700' : 'bg-orange-500'
                  }`}
                >
                  {message.role === 'user' ? (
                    <User className="w-5 h-5 text-white" />
                  ) : (
                    <Bot className="w-5 h-5 text-white" />
                  )}
                </div>

                <div className={`flex-1 ${message.role === 'user' ? 'text-right' : ''}`}>
                  <div
                    className={`inline-block px-6 py-4 rounded-lg max-w-[85%] ${
                      message.role === 'user'
                        ? 'bg-slate-700 text-white'
                        : 'bg-slate-750 border border-slate-600 text-slate-200'
                    }`}
                  >
                    {(message.image || message.image_base64) && (
                      <img
                        src={message.image || message.image_base64}
                        alt="attached"
                        className="max-w-xs max-h-64 rounded mb-2 border border-slate-600"
                        data-testid={`chat-message-image-${index}`}
                      />
                    )}
                    {message.content && (
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-2">
                    {new Date(message.timestamp).toLocaleTimeString(language === 'el' ? 'el-GR' : 'en-US')}
                  </div>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex items-start space-x-3" data-testid="chat-loading">
                <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="bg-slate-750 border border-slate-600 px-6 py-4 rounded-lg">
                  <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
                </div>
              </div>
            )}
          </div>

          <div ref={messagesEndRef} />
        </div>

        {/* Escalation banner */}
        {messages.length >= 2 && (
          <div className="mb-4 flex justify-center">
            {escalated ? (
              <div
                data-testid="chat-escalated-banner"
                className="bg-green-600/15 border border-green-600 text-green-400 px-6 py-3 flex items-center space-x-2"
              >
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-bold">
                  {t({
                    el: 'Το αίτημά σας στάλθηκε. Θα σας καλέσουμε σύντομα.',
                    en: 'Your request was sent. We will call you soon.',
                  })}
                </span>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setEscalateOpen(true)}
                data-testid="chat-escalate-btn"
                className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 font-bold tracking-wider hover:opacity-90 transition-opacity flex items-center space-x-2 shadow-lg"
              >
                <Phone className="w-5 h-5" />
                <span>
                  {t({
                    el: 'ΖΗΤΗΣΤΕ ΤΕΧΝΙΚΟ',
                    en: 'REQUEST TECHNICIAN',
                  })}
                </span>
              </button>
            )}
          </div>
        )}

        {/* Pending image preview */}
        {pendingImage && (
          <div
            className="bg-slate-800 border border-orange-500 p-3 mb-4 flex items-center space-x-3"
            data-testid="chat-image-preview"
          >
            <img
              src={pendingImage.dataUrl}
              alt="preview"
              className="w-16 h-16 object-cover rounded"
            />
            <div className="flex-1">
              <div className="text-white text-sm font-semibold flex items-center">
                <ImageIcon className="w-4 h-4 mr-2 text-orange-400" />
                {pendingImage.name}
              </div>
              <div className="text-slate-400 text-xs">
                {t({ el: 'Έτοιμο για αποστολή', en: 'Ready to send' })}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPendingImage(null)}
              data-testid="chat-image-remove-btn"
              className="text-slate-400 hover:text-red-400 p-2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Input Form */}
        <form onSubmit={handleSendMessage} className="bg-slate-800 border border-slate-700 p-4" data-testid="chat-input-form">
          <div className="flex space-x-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileSelect}
              data-testid="chat-file-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
              data-testid="chat-attach-btn"
              title={t({ el: 'Επισύναψη φωτογραφίας', en: 'Attach photo' })}
              className="bg-slate-700 text-white px-4 py-4 hover:bg-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <Paperclip className="w-5 h-5" />
            </button>

            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={t({
                el: 'Πληκτρολογήστε το μήνυμά σας...',
                en: 'Type your message...',
              })}
              disabled={isLoading}
              data-testid="chat-input-field"
              className="flex-1 bg-slate-700 border border-slate-600 text-white px-6 py-4 focus:outline-none focus:ring-2 focus:ring-orange-500 placeholder-slate-400"
            />
            <button
              type="submit"
              disabled={isLoading || (!inputMessage.trim() && !pendingImage)}
              data-testid="chat-send-btn"
              className="bg-orange-500 text-white px-8 py-4 font-bold tracking-wider hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <span>{t({ el: 'ΑΠΟΣΤΟΛΗ', en: 'SEND' })}</span>
              <Send className="w-5 h-5" />
            </button>
          </div>
        </form>
      </div>

      {/* Escalation Dialog */}
      {escalateOpen && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4"
          onClick={() => !escSubmitting && setEscalateOpen(false)}
          data-testid="chat-escalate-dialog"
        >
          <div
            className="bg-slate-800 border border-slate-700 max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">
                {t({ el: 'Στείλτε αίτημα στον τεχνικό', en: 'Send request to technician' })}
              </h3>
              <button
                type="button"
                onClick={() => !escSubmitting && setEscalateOpen(false)}
                data-testid="chat-escalate-close"
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              {t({
                el: 'Συμπληρώστε τα στοιχεία σας. Θα σταλεί email στον τεχνικό μαζί με την συνομιλία σας και θα σας καλέσουμε.',
                en: 'Fill in your details. An email will be sent to the technician with your conversation and we will call you.',
              })}
            </p>
            <form onSubmit={handleEscalate} className="space-y-3" data-testid="chat-escalate-form">
              <div>
                <label className="block text-slate-300 text-sm mb-1">
                  {t({ el: 'Όνομα', en: 'Name' })} *
                </label>
                <input
                  type="text"
                  required
                  value={escName}
                  onChange={(e) => setEscName(e.target.value)}
                  data-testid="chat-escalate-name"
                  className="w-full bg-slate-700 border border-slate-600 text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-slate-300 text-sm mb-1">
                  {t({ el: 'Τηλέφωνο', en: 'Phone' })} *
                </label>
                <input
                  type="tel"
                  required
                  value={escPhone}
                  onChange={(e) => setEscPhone(e.target.value)}
                  data-testid="chat-escalate-phone"
                  placeholder="69XXXXXXXX"
                  className="w-full bg-slate-700 border border-slate-600 text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-slate-300 text-sm mb-1">
                  {t({ el: 'Επείγον', en: 'Urgency' })}
                </label>
                <select
                  value={escUrgency}
                  onChange={(e) => setEscUrgency(e.target.value)}
                  data-testid="chat-escalate-urgency"
                  className="w-full bg-slate-700 border border-slate-600 text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="immediate">{t({ el: 'Άμεσα (επείγον)', en: 'Immediate (urgent)' })}</option>
                  <option value="today">{t({ el: 'Σήμερα', en: 'Today' })}</option>
                  <option value="week">{t({ el: 'Αυτή την εβδομάδα', en: 'This week' })}</option>
                </select>
              </div>
              <div>
                <label className="block text-slate-300 text-sm mb-1">
                  {t({ el: 'Σημειώσεις (προαιρετικά)', en: 'Notes (optional)' })}
                </label>
                <textarea
                  rows={2}
                  value={escNotes}
                  onChange={(e) => setEscNotes(e.target.value)}
                  data-testid="chat-escalate-notes"
                  className="w-full bg-slate-700 border border-slate-600 text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <button
                type="submit"
                disabled={escSubmitting}
                data-testid="chat-escalate-submit"
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 font-bold tracking-wider disabled:opacity-50 flex items-center justify-center"
              >
                {escSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : t({ el: 'ΑΠΟΣΤΟΛΗ', en: 'SEND' })}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
